import React from "react";
import {observer} from "mobx-react";

import "./station-tabs.scss";

import {Header, Icon, Menu, StrictTabProps, Tab} from "semantic-ui-react";
import {isMobileOnly} from "react-device-detect";

import {Kiwis, TimeSeries, Station} from "../../../../kiwis";
import {ChartConfiguration} from "../../../../config/chart-configuration";

import {TimeSeriesTabPane} from "./time-series-tab-pane/time-series-tab-pane";
import {StationTabPane} from "./station-tab-pane/station-tab-pane";
import {MeasurementsTabPane} from "./measurements-tab-pane/measurements-tab-pane";
import {
    StationPopupConfiguration,
    StationPopupTabs,
    StationPopupTypeConfiguration
} from "../../../../config/station-popup-configuration";
import {DocumentsTabPane} from "./documents-tab-pane/documents-tab-pane";
import {ImagesTabPane} from "./images-tab-pane/images-tab-pane";

import "../../../../utils/evaluate";


interface StationTabsProps {
    kiwis: Kiwis;
    chartConfig: ChartConfiguration;
    stationPopupConfig: StationPopupConfiguration;
    station: Station;
    parentWidth: number | string;
    parentHeight: number | string;
    aspectRatio: number; // aspect ratio of parent windows
    isFullScreen: boolean;
    isDocked: boolean;
    activeTab: number;
    onTabChange: any;
}

@observer
export class StationTabs extends React.Component<StationTabsProps, any> {
    constructor(props: StationTabsProps) {
        super(props);
        this.state = {
            updating: false,
            currentImageIndex: 0 // index of visible image
        };

        this.tabChange = this.tabChange.bind(this);
        this.handleImageChange = this.handleImageChange.bind(this);
    }

    /**
     * Return with of the parent window
     */
    get width() {
        const w = this.props.parentWidth;
        if (w === "100%") {
            // use window width
            return window.innerWidth;
        }
        return typeof w === "string" ?  parseInt(w, 10) : w;
    }

    /**
     * Return height of the parent window
     */
    get height() {
        const h = this.props.parentHeight;
        if (h === "100%") {
            // use window width
            return window.innerHeight;
        }
        return typeof h === "string" ?  parseInt(h, 10) : h;
    }

    /**
     * Get optimal panel width
     * Based on parent width, window.innerWidth and full screen mode
     */
    get paneWidth() {
        if (this.props.isFullScreen) {
            return 15;
        }
        const w = this.width;
        if (w < 698) {
            // only icons shown
            return 13;
        }
        if (w >= 1700) {
            return 14;
        }
        if (w >= 1200) {
            return 13;
        }
        if (w >= 992) {
            return 12;
        }
        if (w >= 768) {
            return 11;
        }
        if (w >= 698) {
            return 10;
        }
        return 12;
    }

    /**
     * Suggestion, if tab menu should be displayed vertically
     * Based on parent width and parent height
     */
    get isMenuVertical() {
        // Do not show vertical menu on mobiles
        if (isMobileOnly || this.props.isFullScreen) {
            // always show top menu on mobiles
            return false;
        }
        const w = this.width;
        const h = this.height;
        if (h < 580) {
            // no space for horizontal top menu
            return true;
        }
        // top space available and enough space on the left for the vertical menu
        return w >= 992;
    }

    /**
     * If menu details should be displayed
     */
    get showMenuDetails() {
        const w = this.width;
        if (!this.isMenuVertical && w < 992) {
            return false;
        }
        return !this.props.isFullScreen && w >= 698;
    }

    componentDidMount() {
    }

    componentWillUnmount() {
        // TODO remove watches
    }

    tabChange(event: React.MouseEvent<HTMLDivElement>, data: StrictTabProps) {
        this.props.onTabChange(event, data);
    }

    handleImageChange(imageIndex: number) {
        this.setState({currentImageIndex: imageIndex});
    }

    mapOrder(array: any[], sorter: any[], key: string) {
        const order = sorter.reduce((r, k, i) => (r[k] = i + 1, r), {});
        return array.sort((a, b) => (order[a[key]] || Infinity) - (order[b[key]] || Infinity));
    }

    render() {
        const panes: any[] = [];
        const {baseId, id, topic} = this.props.station;
        const paneWidth = this.paneWidth;
        const isMenuVertical = this.isMenuVertical;
        const showMenuDetails = this.showMenuDetails;

        const { kiwis, stationPopupConfig, chartConfig, station, aspectRatio, activeTab } = this.props;
        const {currentImageIndex} = this.state;

        // eslint-disable-next-line arrow-body-style
        let timeSeries = [...this.props.kiwis.timeSeriesList.values()].filter((ts: TimeSeries) => {
            return ts.stationId === id && ts.topics.indexOf(topic) !== -1;
        }).sort(TimeSeries.compare); // base sort alphabetically

        // sorting defined on time series?
        const sorter = stationPopupConfig.timeSeriesSorting.filter((tss) => tss.baseId === baseId )[0];
        if (sorter) {
            timeSeries = this.mapOrder(timeSeries, sorter.stationParameterNames, "stationParameterName");
        }

        let menuItemCss = "geo-station-tabs-menu-item";
        if (this.width <= 320) {
            menuItemCss += " xs";
        } else if (this.width < 420) {
            menuItemCss += " sm";
        }
        if (!showMenuDetails) {
            menuItemCss += " compact";
        }

        // is a popup type defined for this station
        const popupType = stationPopupConfig.types.find((type: StationPopupTypeConfiguration) => {
            const template = type.expression;
            try {
                return template.evaluate(station);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error evaluating popup type expression.");
            }
            return false;
        });
        let visibleTabs = null;
        if (popupType && popupType.tabs) {
            visibleTabs = popupType.tabs;
        }

        if (!visibleTabs || visibleTabs.includes(StationPopupTabs.Info)) {
            // add main pane with information about the station
            const isActive = panes.length === activeTab;
            const infoTab = this.InfoTab(station, menuItemCss, showMenuDetails, isActive, this.props);
            panes.push(infoTab);
        }

        if (!visibleTabs || visibleTabs.includes(StationPopupTabs.Measurements)) {
            // add current measurements
            const isActive = panes.length === activeTab;
            const measurementsTab = this.MeasurementsTab(station, menuItemCss, showMenuDetails, isActive, this.props);
            panes.push(measurementsTab);
        }

        if (!visibleTabs || visibleTabs.includes(StationPopupTabs.TimeSeries)) {
            // add pane for each measurement parameter
            timeSeries.forEach((ts) => {
                let label = ts.name;
                if (ts.stationParameterName && ts.name !== ts.parameterTypeName) {
                    label = ts.stationParameterName;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const iconName = stationPopupConfig.icons[ts.stationParameterName];

                const menuItem =
                    <Menu.Item key={`station-tab-menu-ts-${ts.id}`}
                        className={`${menuItemCss} ${label.length < 16 ? "compact" : ""}`}>
                        <Icon name={iconName} />
                        {showMenuDetails &&
                        <div className="station-tab-menu-details">
                            <Header as="h4" className={"geo-station-tabs-menu-header"}>
                                {label}
                            </Header>
                        </div>
                        }
                    </Menu.Item>;
                const pane = {
                    menuItem,
                    pane: <TimeSeriesTabPane
                        key={`ts-tab-pane-${ts.id}`}
                        kiwis={kiwis}
                        chartConfig={chartConfig}
                        parentWidth={this.width}
                        parentHeight={this.height}
                        aspectRatio={aspectRatio}
                        active={panes.length === activeTab}
                        timeSeries={ts}/>
                };
                panes.push(pane);
            });
        }

        if (!visibleTabs || visibleTabs.includes(StationPopupTabs.Documents)) {
            // add documents pane
            const isActive = panes.length === activeTab;
            const docTab = this.DocumentsTab(station, menuItemCss, showMenuDetails, isActive, this.props);
            panes.push(docTab);
        }

        if (!visibleTabs || visibleTabs.includes(StationPopupTabs.Images)) {
            // add images pane
            const isActive = panes.length === activeTab;
            const docTab = this.ImagesTab(
                station,
                menuItemCss,
                showMenuDetails,
                isActive,
                currentImageIndex,
                this.props
            );
            panes.push(docTab);
        }

        const className = `geo-station-tabs ui segment
            ${this.isMenuVertical ? "vertical-menu" : "top-menu"}
            ${isMobileOnly ? "is-mobile-only" : ""}`;

        return <Tab className={className}
            menu={{fluid: true, vertical: isMenuVertical, pointing: true, secondary: true}}
            panes={panes}
            activeIndex={activeTab}
            grid={{
                paneWidth,
                tabWidth: 16 - paneWidth
            }}
            onTabChange={this.tabChange}
            renderActiveOnly={false}
            size={"small"}/>;
    }

    /**
     * Tab with main information
     *
     * @param station
     * @param css
     * @param showMenuDetails
     * @param active
     * @param props
     */
    private InfoTab(station: Station, css: string, showMenuDetails: boolean, active: boolean, props: StationTabsProps) {

        const {kiwis, aspectRatio, isFullScreen, isDocked, stationPopupConfig} = props;

        return {
            menuItem:
                <Menu.Item key={`station-tab-menu-info-${station.id}`}
                    className={css}>
                    <Icon name="info" />
                    {showMenuDetails &&
                    <div className="station-tab-menu-details" style={{display: showMenuDetails ? "block" : "none"}}>
                        <Header as="h4">Stationsinfo</Header>
                        <p className={"geo-station-tabs-menu-label"}>Datenblatt</p>
                    </div>
                    }
                </Menu.Item>,
            pane: <StationTabPane
                key={`station-tab-pane-${station.id}`}
                kiwis={kiwis}
                station={station}
                parentWidth={this.width}
                parentHeight={this.height}
                aspectRatio={aspectRatio}
                isFullScreen={isFullScreen}
                active={active}
                isDocked={isDocked}
                config={stationPopupConfig.stationInfo}/>
        };
    }

    /**
     * Tab with current measurements
     *
     * @param station
     * @param css
     * @param showMenuDetails
     * @param active
     * @param props
     */
    private MeasurementsTab(station: Station, css: string, showMenuDetails: boolean, active: boolean,
                            props: StationTabsProps) {

        const {kiwis, aspectRatio, stationPopupConfig} = props;

        return {
            menuItem:
                <Menu.Item key={`measurements-tab-menu-info-${station.id}`} className={css}>
                    <Icon name="chart bar outline" />
                    {showMenuDetails &&
                    <div className="station-tab-menu-details">
                        <Header as="h4">Messwerte / Beurteilungen</Header>
                        <p className={"geo-station-tabs-menu-label"}>Aktuellste Daten</p>
                    </div>
                    }
                </Menu.Item>,
            pane: <MeasurementsTabPane
                key={`measurements-tab-pane-${station.id}`}
                kiwis={kiwis}
                station={station}
                aspectRatio={aspectRatio}
                active={active}
                config={stationPopupConfig.measurementsInfo}/>
        };
    }

    /**
     * Tab with documents
     *
     * @param station
     * @param css
     * @param showMenuDetails
     * @param active
     * @param props
     */
    private DocumentsTab(station: Station, css: string, showMenuDetails: boolean, active: boolean,
                         props: StationTabsProps) {

        const {kiwis} = props;

        return {
            menuItem: <Menu.Item key={`documents-tab-menu-info-${station.id}`} className={css}>
                <Icon name="file alternate outline" />
                {showMenuDetails &&
                <div className="station-tab-menu-details">
                    <Header as="h4">Dokumente</Header>
                    <p className={"geo-station-tabs-menu-label"}>Stammdatenblatt, Jahrbücher, Berichte</p>
                </div>
                }
            </Menu.Item>,
            pane: <DocumentsTabPane
                key={`documents-tab-pane-${station.id}`}
                station={station}
                kiwis={kiwis}
                active={active}
            />
        };
    }

    /**
     * Tab with images
     *
     * @param station
     * @param css
     * @param showMenuDetails
     * @param active
     * @param currentImageIndex
     * @param props
     */
    private ImagesTab(station: Station, css: string, showMenuDetails: boolean, active: boolean,
                      currentImageIndex: number, props: StationTabsProps) {

        const {kiwis} = props;

        return {
            menuItem: <Menu.Item key={`images-tab-menu-info-${station.id}`} className={css}>
                <Icon name="picture" />
                {showMenuDetails &&
                <div className="images-tab-menu-details">
                    <Header as="h4">Darstellungen</Header>
                    <p className={"geo-station-tabs-menu-label"}>Stationsfoto, Grafiken, Karten</p>
                </div>
                }
            </Menu.Item>,
            pane: <ImagesTabPane
                key={`images-tab-pane-${station.id}`}
                station={station}
                kiwis={kiwis}
                active={active}
                currentImageIndex={currentImageIndex}
                onImageChange={this.handleImageChange}
            />
        };
    }

}
